import { useLang } from "@/i18n/LanguageContext";
import { LANGS, LANG_LABEL, LANG_FULL, type Lang } from "@/i18n/translations";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, ChevronDown } from "lucide-react";

interface Props {
  className?: string;
  /** "compact" shows EN, "full" shows English. */
  variant?: "compact" | "full";
  align?: "start" | "center" | "end";
}

const LanguageSwitcher = ({ className, variant = "compact", align = "end" }: Props) => {
  const { lang, switchTo } = useLang();

  const triggerLabel = variant === "compact" ? LANG_LABEL[lang] : LANG_FULL[lang];

  return (
    <DropdownMenu>
      {/* aria-label includes the visible text so screen readers announce both
          the action ("Select language") and current value (e.g. "EN"), and so
          axe's label-content-name-mismatch check passes. */}
      <DropdownMenuTrigger
        aria-label={`Select language, current: ${LANG_FULL[lang]}`}
        className={cn(
          "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm",
          className
        )}
      >
        <span>{triggerLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[10rem]">
        {LANGS.map((l: Lang) => {
          const active = l === lang;
          return (
            <DropdownMenuItem
              key={l}
              onSelect={() => !active && switchTo(l)}
              className={cn(
                "flex cursor-pointer items-center justify-between text-sm",
                active && "font-semibold"
              )}
            >
              <span>{LANG_FULL[l]}</span>
              {active && <Check className="h-4 w-4 opacity-70" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
